import { useState } from "react";

const slides = [
  {
    id: 1,
    image:
      "/images/Refrito_de_Atun.png",
    title: "Refrito de Atún",
    description: "Sautéed tuna with onions, and tomatoes. A traditional Ecuadorian comfort dish served with rice and fried plantains.",
    ingredients: [ 
      "2 cans (175gm) of tuna in oil or water",
      "1 red onion, finely diced",
      "1 tomato, diced",
      "1 green bell pepper, finely chopped",
      "2 garlic cloves, minced",
      "2 tablespoons achiote oil or olive oil",
      "1 teaspoon ground cumin",
      "Salt and black pepper to taste",
      "Fresh cilantro, chopped",
      "Optional: cooked white rice, avocado, fried plantains for serving"
    ],
    instructions: [
      "Heat the achiote oil in a frying pan over medium heat.",
      "Add the garlic, onion and bell pepper, sauté for 3–4 minutes until softened.",
      "Stir in the tomato, cooking until the tomato breaks down into a sauce.",
      "Add cumin, salt, and pepper.",
      "Drain the tuna lightly and add it to the pan. Mix gently and cook for another 2–3 minutes.",
      "Sprinkle fresh cilantro on top before serving.",
      "Serve hot with white rice, avocado, or fried plantains."
    ],
    servings: "3 people",
    time: "20 minutes"
  },
  {
    id: 2,
    image:
      "/images/Tuna_Melt_Sandwich.png",
    title: "Tuna Melt Sandwich",
    description: "A crispy, golden grilled sandwich filled with creamy tuna salad and melted cheddar cheese, combining rich flavors and crunchy texture in every bite.",
    ingredients: [ 
      "2 cans (175gm) of tuna in oil or water",
      "2 cans albacore tuna",
      "3 tablespoons mayonnaise",
      "1 teaspoon Dijon mustard",
      "1 tablespoon lemon juice",
      "2 tablespoons chopped pickles",
      "2 tablespoons finely diced red onions",
      "Salt and black pepper to taste",
      "Optional: green onions, chives, capers, or celery for extra crunch",
      "4 slices sourdough or sandwich bread",
      "2–4 slices sharp cheddar cheese",
      "Butter, for spreading on bread"
    ],
    instructions: [
      "In a bowl, use a fork to flake the tuna apart.",
      "Add mayonnaise, Dijon mustard, lemon juice, pickles, red onions, salt, and pepper. Mix until well combined.",
      "Spread a thin layer of butter on one side of each bread slice.",
      "Spread the tuna salad evenly over the unbuttered sides of two bread slices.",
      "Top with 1–2 slices of cheddar cheese.",
      "Place the remaining bread slices on top with the buttered sides facing outward.",
      "Heat a large skillet over medium-low heat.",
      "Place the sandwiches in the skillet, cover with a lid, and cook for 3–4 minutes until golden brown.",
      "Flip carefully with a spatula and cook another 3–4 minutes until the cheese is melted and the bread is crispy and golden.",
      "Serve hot and enjoy."
    ],
    servings: "3 people",
    time: "18 minutes"
  }
];

export default function RecipeCarousel() {
  const [current, setCurrent] = useState(0);

  const nextSlide = () =>
    setCurrent((prev) => (prev + 1) % slides.length);

  const prevSlide = () =>
    setCurrent((prev) =>
      prev === 0 ? slides.length - 1 : prev - 1
    );

  return (
    <div className="w-full">
      <h2 className="text-4xl font-bold text-center text-blue-900 mb-8">
        Delicious Tuna Recipes
      </h2>
      <div className="relative w-full h-[600px] md:h-[400px] overflow-hidden rounded-xl shadow-2xl">
        {/* Slides */}
        <div
          className="flex h-full transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slides.map((slide) => (
            <div
              key={slide.id}
              className="min-w-full h-full flex flex-col md:flex-row"
            >
              {/* Image div */}
              <div
                className="w-full md:w-2/5 h-32 md:h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.image})` }}
              />

              {/* Recipe div */}
              <div className="w-full md:w-3/5 h-full bg-white p-6 overflow-y-auto">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4">
                  {slide.title}
                </h2>
                <p className="text-gray-700 text-base mb-4">
                  {slide.description}
                </p>
                <div className="flex flex-wrap gap-4 mb-4 text-gray-600 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⏱️</span>
                    <span>{slide.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">👥</span>
                    <span>{slide.servings}</span>
                  </div>
                </div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Ingredients
                  </h3>
                  <p className="text-gray-700 bg-blue-50 p-3 rounded-lg">
                    {slide.ingredients.map((ingredients, index) => (
                      <span key={index} className="flex items-center gap-2 mb-2">
                        <span className="text-sm">{ingredients}</span>
                      </span>
                    ))}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Instructions
                  </h3>
                  <ol className="space-y-2 text-gray-700">
                    {slide.instructions.map((instruction, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <span className="text-sm">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white px-4 py-2 rounded-full text-2xl transition z-10"
        >
          ‹
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white px-4 py-2 rounded-full text-2xl transition z-10"
        >
          ›
        </button>

        {/* Dots indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className={`w-3 h-3 rounded-full transition ${
                index === current ? "bg-blue-600" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Recipe counter */}
      <div className="text-center mt-4 text-gray-600">
        Recipe {current + 1} of {slides.length}
      </div>
    </div>
  );
}
